(() => {
  if (customElements.get('mirolyub-lookbook')) return;
  class MirolyubLookbook extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.track = this.querySelector('[data-book-track]');
      this.pages = [...this.querySelectorAll('[data-book-page]')];
      this.mobile = matchMedia('(max-width:749px)');
      this.reducedMotion = matchMedia('(prefers-reduced-motion:reduce)');
      this.activeHotspot = null;
      this.animating = false;
      this.querySelectorAll('[data-book-prev]').forEach(button => button.addEventListener('click', () => this.turn(-1)));
      this.querySelectorAll('[data-book-next]').forEach(button => button.addEventListener('click', () => this.turn(1)));
      this.querySelectorAll('[data-book-goto]').forEach(button => button.addEventListener('click', () => this.go(Number(button.dataset.bookGoto))));
      this.track.addEventListener('scroll', () => this.updatePages(), { passive: true });
      this.mobile.addEventListener('change', () => {
        this.closeItem(false);
        const width = this.pages[0]?.getBoundingClientRect().width || 1;
        const step = this.step();
        const target = Math.max(0, Math.min(Math.floor(this.current() / step) * step, this.pages.length - step));
        this.track.scrollTo({ left: target * width, behavior: 'instant' });
        this.updatePages();
      });
      this.track.addEventListener('dragstart', event => event.preventDefault());
      let drag = null;
      this.track.addEventListener('pointerdown', event => {
        if (!event.isPrimary || this.animating || event.target.closest('a,button,input,select,.ml-book-item') || (event.pointerType === 'mouse' && event.button !== 0)) return;
        drag = { id: event.pointerId, x: event.clientX, y: event.clientY, axis: null };
      });
      this.track.addEventListener('pointermove', event => {
        if (!drag || drag.id !== event.pointerId) return;
        const dx = event.clientX - drag.x, dy = event.clientY - drag.y;
        if (!drag.axis) {
          if (Math.max(Math.abs(dx), Math.abs(dy)) < 12) return;
          if (Math.abs(dy) > Math.abs(dx)) { drag = null; return; }
          if (Math.abs(dx) < Math.abs(dy) * 1.2) return;
          drag.axis = 'x'; this.track.setPointerCapture(event.pointerId);
        }
        if (event.cancelable) event.preventDefault();
      });
      this.track.addEventListener('pointerup', event => {
        if (!drag || drag.id !== event.pointerId) return;
        const dx = event.clientX - drag.x, horizontal = drag.axis === 'x';
        if (this.track.hasPointerCapture(event.pointerId)) this.track.releasePointerCapture(event.pointerId);
        drag = null;
        if (horizontal && Math.abs(dx) > 45) this.turn(dx < 0 ? 1 : -1);
      });
      this.track.addEventListener('pointercancel', () => { drag = null; });
      this.track.addEventListener('keydown', event => {
        if (event.target !== this.track || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();
        if (event.key === 'Home') this.go(0);
        else if (event.key === 'End') this.go(this.pages.length - 1);
        else this.turn(event.key === 'ArrowRight' ? 1 : -1);
      });
      this.querySelectorAll('[data-item-target]').forEach(hotspot => {
        hotspot.setAttribute('role', 'button');
        hotspot.addEventListener('click', event => { event.preventDefault(); this.showItem(hotspot); });
        hotspot.addEventListener('keydown', event => {
          if (event.key === ' ') { event.preventDefault(); hotspot.click(); }
        });
      });
      this.querySelectorAll('[data-close-item]').forEach(button => button.addEventListener('click', () => this.closeItem(true)));
      this.addEventListener('keydown', event => { if (event.key === 'Escape') this.closeItem(true); });
      this.addEventListener('click', event => {
        if (!event.target.closest('.ml-book-item,[data-item-target]')) this.closeItem(false);
      });
      this.querySelectorAll('.ml-book-item').forEach(card => this.prepareItem(card));
      this.querySelectorAll('[data-book-motion]').forEach(button => button.addEventListener('click', () => {
        const page = button.closest('[data-book-page]'), video = page.querySelector('[data-book-video]');
        if (page.dataset.motionPlaying === 'true') { this.stopMotion(page); return; }
        this.closeItem(false); page.dataset.motionPlaying = 'true'; video.hidden = false;
        page.querySelectorAll('[data-item-target]').forEach(dot => { dot.tabIndex = -1; dot.setAttribute('aria-hidden','true'); });
        button.setAttribute('aria-pressed','true'); button.textContent = button.dataset.stopLabel + ' ▪';
        video.play().catch(() => this.stopMotion(page));
      }));
      this.querySelectorAll('[data-book-video]').forEach(video => { video.addEventListener('ended', () => this.stopMotion(video.closest('[data-book-page]'))); video.addEventListener('error', () => this.stopMotion(video.closest('[data-book-page]'))); });
      this.track.addEventListener('wheel', event => {
        if (event.ctrlKey || Math.abs(event.deltaX) < 25 || Math.abs(event.deltaX) <= Math.abs(event.deltaY)) return;
        event.preventDefault(); if (!this.animating) this.turn(event.deltaX > 0 ? 1 : -1);
      }, { passive: false });
      this.updatePages();
    }
    stopMotion(page) {
      const video=page.querySelector('[data-book-video]'), button=page.querySelector('[data-book-motion]');
      if (!video || !button) return;
      video.pause(); if (video.readyState) video.currentTime=0; video.hidden=true; page.dataset.motionPlaying='false';
      page.querySelectorAll('[data-item-target]').forEach(dot=>{dot.tabIndex=0;dot.removeAttribute('aria-hidden');});
      button.setAttribute('aria-pressed','false');button.textContent=button.dataset.playLabel+' ▷';
    }
    step() { return this.mobile.matches ? 1 : 2; }
    current() {
      const width = this.pages[0]?.getBoundingClientRect().width || 1;
      return Math.round(this.track.scrollLeft / width);
    }
    go(index) {
      if (this.animating) return;
      const step = this.step();
      const current = this.current();
      const target = Math.max(0, Math.min(Math.floor(index / step) * step, this.pages.length - step));
      if (target === current) { this.updatePages(); return; }
      this.closeItem(false);
      this.pages.forEach(page => this.stopMotion(page));
      const width = this.pages[0]?.getBoundingClientRect().width || 1;
      if (this.reducedMotion.matches) {
        this.track.scrollTo({ left: target * width, behavior: 'instant' });
        this.updatePages(); return;
      }
      const forward = target > current;
      const shell = this.querySelector('.ml-book-shell');
      const oldFace = this.pages[forward ? current + step - 1 : current];
      const newFace = this.pages[forward ? target : target + step - 1];
      const ghost = page => {
        const copy = page.cloneNode(true);
        copy.removeAttribute('data-book-page'); copy.removeAttribute('inert'); copy.inert = true; copy.removeAttribute('data-shopify-editor-block');
        copy.querySelectorAll('.ml-book-item,.ml-look-hotspot,.ml-book-corner').forEach(element => element.remove());
        copy.querySelectorAll('[id]').forEach(element => element.removeAttribute('id'));
        return copy;
      };
      const turn = document.createElement('div');
      turn.className = 'ml-book-turn ml-book-turn--' + (forward ? 'forward' : 'backward');
      turn.setAttribute('aria-hidden', 'true');
      for (const [page, side] of [[oldFace, 'front'], [newFace, 'back']]) {
        const face = document.createElement('div'); face.className = 'ml-book-face ml-book-face--' + side;
        face.append(ghost(page)); turn.append(face);
      }
      let still = null;
      if (step === 2) {
        still = document.createElement('div'); still.className = 'ml-book-still'; still.setAttribute('aria-hidden', 'true');
        still.style[forward ? 'left' : 'right'] = '0';
        still.append(ghost(this.pages[forward ? current : current + 1])); shell.append(still);
      }
      shell.append(turn);
      this.animating = true; this.dataset.turning = 'true';
      this.track.scrollTo({ left: target * width, behavior: 'instant' });
      this.updatePages();
      const animation = turn.animate([{ transform: 'rotateY(0deg)' }, { transform: 'rotateY(' + (forward ? '-180' : '180') + 'deg)' }], { duration: 680, easing: 'cubic-bezier(.35,.1,.2,1)', fill: 'forwards' });
      const complete = () => {
        turn.remove(); still?.remove(); this.animating = false; this.dataset.turning = 'false'; this.updatePages();
      };
      animation.addEventListener('finish', complete, { once: true });
      animation.addEventListener('cancel', complete, { once: true });
    }
    turn(direction) { this.go(this.current() + direction * this.step()); }
    updatePages() {
      const first = Math.min(this.current(), this.pages.length - this.step());
      const last = Math.min(first + this.step(), this.pages.length);
      this.pages.forEach((page, index) => { page.inert = index < first || index >= last; });
      this.querySelector('[data-book-count]').textContent = this.step() === 1 ? `${first + 1} / ${this.pages.length}` : `${first + 1}–${last} / ${this.pages.length}`;
      this.querySelectorAll('[data-book-prev]').forEach(button => { button.disabled = first <= 0; });
      this.querySelectorAll('[data-book-next]').forEach(button => { button.disabled = last >= this.pages.length; });
      const open = this.querySelector('.ml-book-item:not([hidden])');
      if (open && open.closest('[data-book-page]').inert) this.closeItem(false);
    }
    closeItem(returnFocus) {
      this.querySelectorAll('.ml-book-item').forEach(card => { card.hidden = true; });
      this.querySelectorAll('[data-item-target]').forEach(hotspot => hotspot.setAttribute('aria-expanded', 'false'));
      if (returnFocus) this.activeHotspot?.focus();
    }
    showItem(hotspot) {
      if (this.animating) return;
      const card = this.querySelector('#' + CSS.escape(hotspot.dataset.itemTarget));
      if (!card) return;
      const wasOpen = !card.hidden;
      this.closeItem(false);
      this.activeHotspot = hotspot;
      if (wasOpen) return;
      card.hidden = false;
      hotspot.setAttribute('aria-expanded', 'true');
      const pageRect = hotspot.closest('[data-book-page]').getBoundingClientRect();
      const dotRect = hotspot.getBoundingClientRect();
      card.style.transform = 'none';
      card.style.left = Math.max(14, Math.min(dotRect.right - pageRect.left + 6, pageRect.width - card.offsetWidth - 14)) + 'px';
      card.style.top = Math.max(16, Math.min(dotRect.bottom - pageRect.top + 4, pageRect.height - card.offsetHeight - 18)) + 'px';
      card.querySelector('[data-close-item]').focus({ preventScroll: true });
    }
    prepareItem(card) {
      const config = JSON.parse(card.querySelector('[data-item-config]').textContent);
      const form = card.querySelector('form');
      const select = card.querySelector('[data-item-size]');
      const button = card.querySelector('[data-item-add]');
      const price = card.querySelector('[data-item-price]');
      const status = card.querySelector('[data-item-status]');
      const money = new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency: config.currency });
      let busy = false;
      let submitted = false;
      const update = () => {
        const option = select.selectedOptions[0];
        button.disabled = busy || submitted || !select.value || !option || option.disabled;
        if (option?.dataset.price) price.textContent = money.format(Number(option.dataset.price) / 100);
      };
      select.addEventListener('change', () => { status.textContent = ''; update(); });
      update();
      form.addEventListener('submit', async event => {
        if (form.dataset.mlVerified === 'true') { delete form.dataset.mlVerified; return; }
        event.preventDefault();
        const option = select.selectedOptions[0];
        if (busy || submitted || !select.value || !option || option.disabled) return;
        busy = true; button.textContent = config.text.adding; status.textContent = ''; update();
        try {
          const root = (window.Shopify?.routes?.root || config.root || '/').replace(/\/?$/, '/');
          const response = await fetch(root + 'products/' + encodeURIComponent(config.handle) + '.js', { credentials: 'same-origin', cache: 'no-store' });
          if (!response.ok) throw new Error(config.text.failed);
          const product = await response.json();
          const variant = product.variants?.find(variant => Number(variant.id) === Number(select.value));
          if (product.handle !== config.handle || !variant || !variant.available) {
            option.disabled = true; select.value = ''; throw new Error(config.text.unavailable);
          }
          if (Number(variant.price) !== Number(option.dataset.price)) {
            option.dataset.price = String(variant.price); status.textContent = config.text.priceChanged; return;
          }
          submitted = true;
          form.dataset.mlVerified = 'true';
          form.requestSubmit();
        } catch (error) {
          status.textContent = error instanceof Error ? error.message : config.text.failed;
        } finally {
          busy = false; button.textContent = config.text.add; update();
        }
      });
    }
  }
  customElements.define('mirolyub-lookbook', MirolyubLookbook);
})();
